import { Handle, Position } from "@xyflow/react";
import { Puzzle } from "lucide-react";
import { componentMatchesOverlay, type OverlayMode, overlayRingClass } from "@/lib/arch-overlay";
import type { Component, ContainerKind } from "@/lib/arch-parser";
import { cn } from "@/lib/utils";
import { useArchViz } from "../ArchVizContext";
import { CommentBadge } from "./CommentBadge";
import { containerPalette } from "./node-colors";
import { RiskBadge } from "./RiskBadge";

export interface ComponentNodeData {
  component: Component;
  // Parent container's canonical kind — used to pick the component's palette
  // so components visually belong to their container. Optional because
  // single-container shorthand callers may omit it; defaults to "other".
  parentKind?: ContainerKind;
  faded?: boolean;
  highlighted?: boolean;
  activeOverlay?: OverlayMode;
  connectsToExternals?: ReadonlySet<string>;
  commentCount?: number;
  carryOverCount?: number;
  [key: string]: unknown;
}

export function ComponentNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: ComponentNodeData;
  selected?: boolean;
}) {
  const { state, dispatch } = useArchViz();
  const palette = containerPalette(data.parentKind);
  const overlay = data.activeOverlay ?? "none";
  const overlayMatch =
    overlay !== "none" &&
    componentMatchesOverlay(data.component, overlay, data.connectsToExternals ?? new Set<string>());
  const riskCount = data.component.risks?.length ?? data.component.risk.length;
  return (
    <div
      className={cn(
        "relative rounded-md border-[1.5px] px-2.5 py-1.5 shadow-sm transition-all min-w-[150px] max-w-[200px]",
        palette.bg,
        palette.border,
        palette.text,
        "opacity-90",
        selected &&
          "ring-2 ring-offset-2 ring-offset-background ring-[color:var(--node-selection-ring)] shadow-md opacity-100 z-10",
        data.faded && "opacity-30",
        data.highlighted && "ring-2 ring-teal-400/70 opacity-100",
        overlayMatch && overlay !== "none" && overlayRingClass(overlay),
      )}
    >
      <CommentBadge count={data.commentCount ?? 0} carryOver={data.carryOverCount ?? 0} />
      {state.showRisks && (
        <RiskBadge
          count={riskCount}
          onClick={() => dispatch({ type: "SELECT_NODE_FOR_RISKS", nodeId: id })}
        />
      )}
      <Handle type="target" position={Position.Top} id="top" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Bottom} id="bottom" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-foreground/30" />
      <Handle type="target" position={Position.Right} id="right" className="!bg-foreground/30" />
      <div className="flex items-center gap-1.5">
        <Puzzle className="h-3 w-3 shrink-0" />
        <div
          className="font-medium leading-none tracking-tight"
          style={{ fontSize: "calc(12px * var(--archviz-label-scale, 1))" }}
        >
          {data.component.label}
        </div>
      </div>
      {(data.component.headline ?? data.component.description) && (
        <div className="text-[10px] text-muted-foreground leading-snug mt-0.5">
          {data.component.headline ?? data.component.description}
        </div>
      )}
      {(() => {
        const techList = data.component.tech_stack ?? data.component.technology;
        if (techList.length === 0) return null;
        return (
          <div className="text-[9px] text-ink-subtle mt-0.5 font-mono">{techList.join(", ")}</div>
        );
      })()}
      <Handle type="source" position={Position.Top} id="top-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom-src"
        className="!bg-foreground/30"
      />
      <Handle type="source" position={Position.Left} id="left-src" className="!bg-foreground/30" />
      <Handle
        type="source"
        position={Position.Right}
        id="right-src"
        className="!bg-foreground/30"
      />
    </div>
  );
}
